import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn
} from 'material-ui/Table';
import Dialog from 'material-ui/Dialog';
import LinearProgress from 'material-ui/LinearProgress';
import SovosPaginationFooter from 's1-ui/sovos-pagination-footer';
import SovosFlatButton from 's1-ui/sovos-flat-button/SovosFlatButton';
import { gridHeaderColorGray } from 'theme/mainColors';
import { RULE_PROP_TYPE } from 'shared/constants/PropTypes';
import ContextualGridMenuOptions from '../../contextualGridMenuOptions/ContextualGridMenuOptions';
import './rulesGrid.scss';

const headerLabelStyle = {
  color: gridHeaderColorGray,
  fontWeight: 400,
  fontSize: 15,
  paddingBottom: 0
};

class RulesGrid extends React.Component {
  constructor(props) {
    super(props);
    this.rulesGridColumns = [
      {
        title: 'Name',
        dataKey: 'name',
        width: 270
      },
      {
        title: 'Description',
        dataKey: 'description'
      },
      {
        title: 'Type',
        dataKey: 'subtype',
        width: 150
      },
      {
        title: '',
        width: 40,
        dataKey: 'moreOptions',
        disabled: !props.showMoreActionsIcon
      }
    ];
    this.state = {
      hoveredRow: -1,
      itemsPerPage: 10,
      page: 0,
      deleteProfileModalOpen: false,
      ruleIdToDelete: -1
    };
  }

  handleRowHover = hoveredRow => this.setState({ hoveredRow });

  handleCellClick = rowID =>
    this.props.onRuleSelect(this.props.rulesListGridData[rowID]);

  getStartItem = () =>
    this.props.totalItems ? this.state.itemsPerPage * this.state.page + 1 : 0;

  calculateEndItem = () => {
    const maximumItemsOnPage = this.state.itemsPerPage * (this.state.page + 1);
    if (maximumItemsOnPage > this.props.totalItems) {
      return this.props.totalItems;
    }
    return maximumItemsOnPage;
  };

  handlePrevPage = ev => {
    ev.preventDefault();
    this.setState(oldState => {
      if (oldState.page > 0) {
        this.props.onPageChange(oldState.page - 1, oldState.itemsPerPage);
        return {
          page: oldState.page - 1
        };
      } else {
        return {};
      }
    });
  };

  handleNextPage = ev => {
    ev.preventDefault();
    this.setState(oldState => {
      if ((oldState.page + 1) * oldState.itemsPerPage < this.props.totalItems) {
        this.props.onPageChange(oldState.page + 1, oldState.itemsPerPage);
        return {
          page: oldState.page + 1
        };
      } else {
        return {};
      }
    });
  };

  handlePerPageChanged = ev => {
    const pageSize = ev.target.value;
    this.setState({ page: 0, itemsPerPage: pageSize });
    this.props.onPageChange(0, pageSize);
  };

  handleEditRule = ruleId => this.props.onEditRule(ruleId);

  handleDeleteRuleClick = ruleIdToDelete =>
    this.setState({ deleteProfileModalOpen: true, ruleIdToDelete });

  handleCloseDeleteRule = () =>
    this.setState({ deleteProfileModalOpen: false, ruleIdToDelete: -1 });

  handleDeleteRule = () => {
    this.props.onDeleteRule(this.state.ruleIdToDelete);
    this.handleCloseDeleteRule();
  };

  render() {
    const {
      rulesListGridData,
      totalItems,
      fetchingRules,
      showPagination,
      displayCheckbox,
      backgroundColor
    } = this.props;
    return (
      <div className="rules-grid__main-container">
        {fetchingRules && (
          <div className="rules-grid__over-layer">
            <LinearProgress mode="indeterminate" />
          </div>
        )}
        <div
          className={ `rules-grid__table-container${
            fetchingRules ? ' blur-content' : ''
          }` }
        >
          {rulesListGridData.length > 0 && (
            <Table
              key="RulesGrid"
              fixedHeader={ true }
              onRowHover={ this.handleRowHover }
              onRowHoverExit={ this.handleRowHoverExit }
              onCellClick={ this.handleCellClick }
              multiSelectable={ true }
              className="rules-grid"
              style={ { backgroundColor: backgroundColor } }
            >
              <TableHeader
                displaySelectAll={ false }
                enableSelectAll={ false }
                adjustForCheckbox={ displayCheckbox }
                style={ { border: 'none' } }
              >
                <TableRow style={ { border: 'none' } }>
                  {this.rulesGridColumns.map(
                    ({ dataKey, width, title, disabled }) =>
                      !disabled && (
                        <TableHeaderColumn
                          key={ dataKey }
                          style={ {
                            width: width || 'auto',
                            ...headerLabelStyle
                          } }
                        >
                          {title}
                        </TableHeaderColumn>
                      )
                  )}
                </TableRow>
              </TableHeader>
              <TableBody
                showRowHover={ true }
                displayRowCheckbox={ displayCheckbox }
                deselectOnClickaway={ false }
              >
                {rulesListGridData.map((rule, index) => (
                  <TableRow
                    id={ `import-rule-grid-row-${index}` }
                    key={ index }
                    hovered={ rule.disabled }
                    hoverable={ !rule.disabled }
                    selectable={ !rule.disabled }
                    selected={ rule.selected }
                    className={ `rules-grid__table-row${
                      rule.disabled
                        ? ' rules-grid__table-row_not-selectable'
                        : ''
                    }` }
                  >
                    {this.rulesGridColumns.map(
                      ({ dataKey, width, disabled }) =>
                        !disabled && (
                          <TableRowColumn
                            key={ dataKey }
                            style={ {
                              width: width || 'auto',
                              textOverflow: `${
                                dataKey !== 'moreOptions' ? 'ellipsis' : 'unset'
                              }`
                            } }
                          >
                            {dataKey !== 'moreOptions'
                              ? rule[dataKey]
                              : this.state.hoveredRow === index && (
                                <ContextualGridMenuOptions
                                  elementId={ rule.id }
                                  onEdit={ this.handleEditRule }
                                  onDelete={ this.handleDeleteRuleClick }
                                  editDisabled={ false }
                                  deleteDisabled={ false }
                                />
                              )}
                          </TableRowColumn>
                        )
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {rulesListGridData.length > 0 && showPagination && (
            <SovosPaginationFooter
              key="RulesPaginationFooter"
              endItem={ this.calculateEndItem() }
              itemsPerPage={ this.state.itemsPerPage }
              startItem={ this.getStartItem() }
              totalItems={ totalItems }
              onPrevClicked={ this.handlePrevPage }
              onNextClicked={ this.handleNextPage }
              onPerPageChanged={ this.handlePerPageChanged }
              itemsPerPageOptions={ [10, 25, 50] }
            />
          )}
          <Dialog
            actions={ [
              <SovosFlatButton
                key="close-delete"
                color="secondary"
                onClick={ this.handleCloseDeleteRule }
              >
                No
              </SovosFlatButton>,
              <SovosFlatButton
                key="submit-delete"
                onClick={ this.handleDeleteRule }
              >
                Yes
              </SovosFlatButton>
            ] }
            modal={ true }
            open={ this.state.deleteProfileModalOpen }
          >
            Are you sure you want to delete this rule?
          </Dialog>
        </div>
      </div>
    );
  }
}

RulesGrid.propTypes = {
  totalItems: PropTypes.number,
  rulesListGridData: PropTypes.arrayOf(RULE_PROP_TYPE).isRequired,
  onRuleSelect: PropTypes.func,
  onDeleteRule: PropTypes.func,
  onEditRule: PropTypes.func,
  onPageChange: PropTypes.func,
  fetchingRules: PropTypes.bool,
  showPagination: PropTypes.bool,
  displayCheckbox: PropTypes.bool,
  showRemoveIcon: PropTypes.bool,
  showMoreActionsIcon: PropTypes.bool,
  backgroundColor: PropTypes.string
};

RulesGrid.defaultProps = {
  fetchingRules: false,
  onRuleSelect: () => null,
  onDeleteRule: () => null,
  onEditRule: () => null,
  totalItems: 0,
  onPageChange: () => null,
  showPagination: false,
  displayCheckbox: false,
  showRemoveIcon: false,
  showMoreActionsIcon: false,
  backgroundColor: '#fff'
};

export default RulesGrid;



// WEBPACK FOOTER //
// ./src/shared/components/rulesGrid/RulesGrid.js